/* ============================================================================
   SEND INVOICE — the bill to the client, as a document of record.

   One tap: the invoice PDF is rendered, filed on the project, and emailed to
   the client with the studio in copy; the outcome lands on the project's
   records so the ledger shows "sent on … to …" or "send failed" plainly.
   ========================================================================== */
import { useState } from "react";
import { Icon } from "./Icons";
import { actions } from "../lib/store";
import { invoiceDoc, pdfBlobUrl } from "../lib/pdf";
import { storeFile } from "../lib/sync";
import { latestRecord, recordState } from "./RecordStatus";
import { fmtDate } from "../lib/format";
import type { Project, Invoice } from "../lib/types";

export function SendInvoiceButton({ p, inv, by, compact }: { p: Project; inv: Invoice; by: string; compact?: boolean }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const rec = latestRecord(p, "invoice-issued", inv.id);
  const st = recordState(rec);
  const to = (p.client.email ?? "").trim();

  async function send() {
    if (busy) return;
    setBusy(true); setErr("");
    try {
      const url = await pdfBlobUrl(invoiceDoc(p, inv));
      const blob = await (await fetch(url)).blob();
      URL.revokeObjectURL(url);
      const name = `${inv.ref.replace(/[^\w]+/g, "_")}_${p.name.replace(/[^\w]+/g, "_")}.pdf`;
      let attachment: { url: string; name: string } | undefined;
      try { attachment = await storeFile(new File([blob], name, { type: "application/pdf" })); } catch { attachment = undefined; }
      actions.markInvoiceSent(p.id, inv.id, attachment);
      actions.record(p.id, { kind: "invoice-issued", refId: inv.id, title: `${inv.ref} — ${inv.title}`, by, attachment });
    } catch (e: any) { setErr(String(e?.message || e).slice(0, 120)); }
    finally { setBusy(false); }
  }

  if (!to) return <span className="dim" style={{ fontSize: 11.5 }}>No client email on the project</span>;
  return (
    <span className="row wrap" style={{ gap: 8, alignItems: "center", justifyContent: "flex-end" }}>
      <button className={"btn sm " + (rec ? "ghost" : "accent")} disabled={busy || st.sending} onClick={() => void send()} title={`Email the invoice PDF to ${to}, studio in copy`}>
        <Icon.send width={13} />{busy || st.sending ? "Sending…" : rec ? "Send again" : "Send to client"}
      </button>
      {!compact && rec && (
        <span className={`badge ${st.tone}`} style={{ fontSize: 9.5 }} title={rec.recipients.map((r) => `${r.party}: ${r.to} — ${r.result}${r.error ? ` (${r.error})` : ""}`).join("\n")}>
          {st.label}{rec.on ? ` · ${fmtDate(rec.on)}` : ""}
        </span>
      )}
      {err && <span style={{ fontSize: 11.5, color: "var(--danger)" }}>{err}</span>}
    </span>
  );
}
